import React, { useState, useEffect, useRef } from 'react';
import '../styles/Gallery.css';

const CATEGORIES = [
  { value: 'hero', label: 'Hero background' },
  { value: 'gallery', label: 'Photo gallery' },
  { value: 'room_master', label: 'Master bedroom' },
  { value: 'room_second', label: 'Second bedroom' },
  { value: 'room_office', label: 'Office / other' }
];

const API_BASE = 'https://cal.mazbluu.com';

export default function Gallery() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadCategory, setUploadCategory] = useState('gallery');
  const fileRef = useRef();
  const token = localStorage.getItem('token');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/gallery', { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setPhotos(data.photos || []);
      } else setError('Failed to load photos');
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 2500); };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setError(''); setUploading(true);

    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('photo', file);
        fd.append('category', uploadCategory);
        const res = await fetch('/api/gallery', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fd
        });
        if (!res.ok) {
          const err = await res.json();
          setError(err.error || 'Upload failed');
          break;
        }
      }
      flash('Photos uploaded');
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const updateCaption = async (id, caption) => {
    try {
      await fetch(`/api/gallery/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ caption })
      });
      flash('Caption saved');
    } catch (err) { setError('Error: ' + err.message); }
  };

  const changeCategory = async (id, category) => {
    try {
      await fetch(`/api/gallery/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ category })
      });
      load();
    } catch (err) { setError('Error: ' + err.message); }
  };

  const move = async (category, index, dir) => {
    const inCat = photos.filter(p => p.category === category);
    const target = index + dir;
    if (target < 0 || target >= inCat.length) return;
    const reordered = [...inCat];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    // optimistic
    setPhotos(photos.map(p => p.category === category
      ? reordered[inCat.findIndex(x => x.id === p.id)] || p : p));
    try {
      await fetch(`/api/gallery/category/${category}/order`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ orderedIds: reordered.map(p => p.id) })
      });
      load();
    } catch (err) { setError('Error: ' + err.message); load(); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this photo? This cannot be undone.')) return;
    try {
      const res = await fetch(`/api/gallery/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { flash('Photo deleted'); load(); }
      else setError('Failed to delete');
    } catch (err) { setError('Error: ' + err.message); }
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading gallery…</div>;

  return (
    <div className="gallery">
      <h2>Photo Gallery</h2>
      <p>Upload and organize photos for the public site. Images are auto-optimized for fast, vibrant display.</p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="gal-upload">
        <select value={uploadCategory} onChange={(e) => setUploadCategory(e.target.value)}>
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
        <input ref={fileRef} type="file" accept="image/*" multiple onChange={handleUpload} disabled={uploading} />
        {uploading && <span className="gal-uploading">Uploading…</span>}
      </div>

      {CATEGORIES.map(cat => {
        const inCat = photos.filter(p => p.category === cat.value);
        return (
          <div key={cat.value} className="gal-category">
            <h3>{cat.label} <span className="gal-count">({inCat.length})</span></h3>
            {inCat.length === 0 ? (
              <p className="gal-empty">No photos in this category.</p>
            ) : (
              <div className="gal-grid">
                {inCat.map((p, idx) => (
                  <div key={p.id} className="gal-item">
                    <img src={`${API_BASE}${p.thumbUrl}`} alt={p.caption || ''} />
                    <input
                      type="text"
                      className="gal-caption"
                      defaultValue={p.caption || ''}
                      placeholder="Caption…"
                      onBlur={(e) => updateCaption(p.id, e.target.value)}
                    />
                    <div className="gal-item-actions">
                      <button disabled={idx === 0} onClick={() => move(cat.value, idx, -1)} title="Move up">▲</button>
                      <button disabled={idx === inCat.length - 1} onClick={() => move(cat.value, idx, 1)} title="Move down">▼</button>
                      <select value={p.category} onChange={(e) => changeCategory(p.id, e.target.value)} title="Change category">
                        {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      <button className="gal-del" onClick={() => remove(p.id)} title="Delete">✕</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
